/**
 * El período contra el anterior del mismo largo («30 días» contra los 30 previos): cuántas campañas y mails salieron,
 * y cómo se movieron las tasas, en puntos. Verde lo que mejoró y rojo lo que empeoró (en rebotes y bajas, subir es
 * empeorar); la cantidad de campañas y de mails no tiene color: más no es mejor.
 *
 * Una tasa que en alguno de los dos períodos no se midió (aperturas apagadas en Snov) no se compara.
 */

import type { ConteosSumados, PeriodoAnterior, Tasas } from "../types";
import { num } from "../../campana-nueva/utils/format";
import { ddmm } from "../../wa-oficial/logic/estadisticas";
import { cambioEnPuntos, cambioRelativo, tasaTexto, type Cambio } from "../logic/emails";

type Sentido = "neutro" | "subir_es_mejor" | "subir_es_peor";

interface Fila {
  label: string;
  ahora: string;
  antes: string;
  cambio: Cambio | null;
  sentido: Sentido;
}

function claseCambio(c: Cambio | null, sentido: Sentido): string {
  if (!c || c.signo === 0 || sentido === "neutro") return "text-slate-500";
  const mejor = sentido === "subir_es_mejor" ? c.signo > 0 : c.signo < 0;
  return mejor ? "text-emerald-700" : "text-red-700";
}

export function Comparacion({ conteos, tasas, anterior }: { conteos: ConteosSumados; tasas: Tasas; anterior: PeriodoAnterior }) {
  const a = anterior.conteos;
  const t = anterior.tasas;
  const tasa = (label: string, ahora: number | null, antes: number | null, sentido: Sentido): Fila => ({
    label, ahora: tasaTexto(ahora), antes: tasaTexto(antes), cambio: cambioEnPuntos(ahora, antes), sentido,
  });
  const filas: Fila[] = [
    { label: "Campañas", ahora: num(conteos.campanas), antes: num(a.campanas), cambio: cambioRelativo(conteos.campanas, a.campanas), sentido: "neutro" },
    { label: "Mails enviados", ahora: num(conteos.enviados), antes: num(a.enviados), cambio: cambioRelativo(conteos.enviados, a.enviados), sentido: "neutro" },
    tasa("Les llegó", tasas.entrega, t.entrega, "subir_es_mejor"),
    tasa("Abrieron", tasas.apertura, t.apertura, "subir_es_mejor"),
    tasa("Hicieron clic", tasas.clic, t.clic, "subir_es_mejor"),
    tasa("Respondieron", tasas.respuesta, t.respuesta, "subir_es_mejor"),
    tasa("Pidieron la baja", tasas.baja, t.baja, "subir_es_peor"),
    tasa("Rebotaron", tasas.rebote, t.rebote, "subir_es_peor"),
  ];
  const visibles = filas.filter((f) => f.cambio !== null || f.sentido === "neutro");
  const sinMedir = filas.filter((f) => f.cambio === null && f.sentido !== "neutro").map((f) => f.label.toLowerCase());

  return (
    <div className="space-y-2">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <caption className="sr-only">Estos días contra los anteriores</caption>
          <thead className="text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            <tr>
              <th className="py-2 pr-3"><span className="sr-only">Qué</span></th>
              <th className="py-2 px-2 text-right">Estos días</th>
              <th className="py-2 px-2 text-right whitespace-nowrap">Del {ddmm(anterior.rango.desde)} al {ddmm(anterior.rango.hasta)}</th>
              <th className="py-2 pl-2 text-right">Cambio</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {visibles.map((f) => (
              <tr key={f.label}>
                <th scope="row" className="py-2 pr-3 text-left font-normal text-slate-700">{f.label}</th>
                <td className="py-2 px-2 text-right font-semibold text-slate-900 tabular-nums">{f.ahora}</td>
                <td className="py-2 px-2 text-right text-slate-500 tabular-nums">{f.antes}</td>
                <td className={`py-2 pl-2 text-right font-semibold tabular-nums whitespace-nowrap ${claseCambio(f.cambio, f.sentido)}`}>{f.cambio?.texto ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-slate-400">
        Las tasas cambian en puntos («+1,5 pp» es de 10 % a 11,5 %).{sinMedir.length ? ` Sin comparar: ${sinMedir.join(", ")} (no se midió en uno de los dos).` : ""}
      </p>
    </div>
  );
}

export default Comparacion;
